import type { Dictionary } from './pt'

export const en: Dictionary = {
  htmlLang: 'en',
  localeName: 'English',

  brand: {
    name: 'DPGen',
    expansion: 'Deterministic Password Generator',
    tagline: 'passwords derived, never stored',
  },

  topbar: {
    theme: 'Toggle theme',
    lang: 'Toggle language',
    source: 'Source code on GitHub',
    skip: 'Skip to the generator',
  },

  hero: {
    badge: 'No vault · no server · no storage',
    titleLead: 'The same password,',
    titleAccent: 'every time',
    titleTail: '— without ever storing it.',
    subtitle:
      'Your password is not saved anywhere: it is recomputed from your master password and the service name using Argon2id. Same inputs, same password, on any device.',
    ctaPrimary: 'Generate my password',
    ctaSecondary: 'How it works',
    facts: [
      { value: 'Argon2id', label: 'memory-hard derivation, 64 MiB per attempt' },
      { value: '0 bytes', label: 'written to your device or to any server' },
      { value: '100%', label: 'in the browser — works offline too' },
    ],
  },

  gen: {
    title: 'Generator',
    subtitle: 'Everything happens on this device. Nothing is sent, nothing is saved.',

    master: {
      label: 'Master password',
      placeholder: 'the only password you have to remember',
      hint: 'Pick something long and unique. If it leaks, every derived password leaks with it.',
      show: 'Show master password',
      hide: 'Hide master password',
    },

    fingerprint: {
      label: 'Fingerprint',
      hint: 'These three symbols depend only on your master password. Memorize them: if they change, you mistyped it.',
      toggle: 'Show fingerprint',
      privacy: 'Do not share or photograph this mark.',
    },

    service: {
      label: 'Service',
      placeholder: 'github.com',
      hint: 'Domain or site name. Case and surrounding spaces are ignored.',
    },

    login: {
      label: 'Login',
      placeholder: 'your.user@example.com',
      hint: 'Account username or email. It may stay empty — but then it must stay empty forever.',
    },

    counter: {
      label: 'Version',
      hint: 'Bump it by 1 to rotate this service’s password without changing your master.',
      decrease: 'Decrease version',
      increase: 'Increase version',
    },

    preset: {
      label: 'Format',
      custom: 'Custom',
      options: {
        compat: 'Compatible',
        standard: 'Standard',
        long: 'Long',
        pin: 'PIN',
        lowercase: 'Letters only',
      },
      descriptions: {
        compat: '12 characters, no symbols — for picky sites',
        standard: '16 characters with symbols — good for almost everything',
        long: '32 characters — maximum resistance',
        pin: '6 digits — padlocks, cards, lock screens',
        lowercase: '20 lowercase letters — easy to type on a TV',
      },
    },

    advanced: {
      show: 'Advanced options',
      hide: 'Hide advanced options',
      warning:
        'Any change here produces a completely different password. Write down what you picked.',
    },

    length: { label: 'Length', unit: (n: number) => `${n} characters` },

    classes: {
      label: 'Allowed characters',
      lower: 'Lowercase',
      upper: 'Uppercase',
      digits: 'Digits',
      symbols: 'Symbols',
      atLeastOne: 'Select at least one set.',
    },

    symbolSet: {
      label: 'Symbol set',
      safe: 'Safe',
      extended: 'Extended',
      safeHint: '!#$%&()*+,-./:;<=>?@[]^_{|}~ — accepted by virtually every site',
      extendedHint: 'adds quotes, backtick, backslash and space — some sites reject these',
    },

    ambiguous: {
      label: 'Avoid ambiguous characters',
      hint: 'Removes O 0 o I l 1 | — useful when you have to read or dictate the password.',
    },

    cost: {
      label: 'Derivation cost',
      fast: 'Fast',
      balanced: 'Balanced',
      high: 'High',
      hints: {
        fast: '32 MiB · 2 passes — older devices',
        balanced: '64 MiB · 3 passes — recommended',
        high: '256 MiB · 4 passes — may fail on phones',
      },
    },

    submit: 'Generate password',
    working: 'Deriving…',
    workingNote: 'The delay is deliberate: it is what stalls anyone trying to guess.',
    clear: 'Clear everything',

    validation: {
      masterRequired: 'Enter your master password.',
      serviceRequired: 'Enter the service.',
    },

    error: {
      title: 'Could not generate',
      memory:
        'Your browser could not reserve the memory this cost profile needs. Try Balanced or Fast — keeping in mind that this changes the generated password.',
      wasm:
        'The Argon2id WebAssembly module failed to load. Reload the page; if it persists, your browser may be blocking WebAssembly.',
      input: 'One of the inputs is out of range.',
      unknown: 'Unexpected error during derivation.',
    },

    result: {
      title: 'Your password',
      reveal: 'Reveal password',
      hide: 'Hide password',
      copy: 'Copy',
      copied: 'Copied!',
      copyFailed: 'Could not copy — select and copy it manually.',
      clipboardNote:
        'We try to clear the clipboard after 30 seconds — not every browser allows it.',
      elapsed: (ms: number) => `derived in ${(ms / 1000).toFixed(1)} s`,
      entropy: (bits: number) => `${Math.round(bits)} bits of entropy`,
      strength: {
        weak: 'Weak',
        fair: 'Fair',
        good: 'Strong',
        excellent: 'Excellent',
      },
      recipe: 'Copy link with these parameters',
      recipeCopied: 'Link copied!',
      recipeNote:
        'The link carries format, version and service — never your master password. It does reveal which services you use, so keep it somewhere private.',
      stale: 'Parameters changed. Generate again to see the matching password.',
    },
  },

  how: {
    title: 'How it works',
    subtitle:
      'Three inputs go in, one password comes out. Always the same — and nothing has to be stored along the way.',
    steps: [
      {
        title: 'You provide what only you know',
        body: 'The master password is the only secret. Service, login and version are public data that merely identify the account.',
      },
      {
        title: 'The browser derives a key',
        body: 'Service, login, version and format become a salt (SHA-256). Argon2id mixes that salt with your master password, spending 64 MiB of memory, and produces 128 bytes.',
      },
      {
        title: 'The bytes become a password',
        body: 'Those 128 bytes are read as one huge integer and converted into the alphabet you chose, guaranteeing at least one character of each required type.',
      },
    ],
    diagram: {
      title: 'Derivation flow',
      master: 'master password',
      secret: 'secret',
      public: 'public',
      service: 'service + login + version + format',
      sha: 'SHA-256',
      salt: 'salt (32 bytes)',
      argon: 'Argon2id',
      argonParams: '64 MiB · 3 passes',
      key: '128 bytes',
      map: 'mapping',
      password: 'password',
      caption:
        'No arrow leaves your device. The whole diagram happens inside this browser tab.',
    },
  },

  sec: {
    title: 'What this protects — and what it does not',
    subtitle: 'No security tool is honest if it only lists its own virtues.',
    protectsTitle: 'Protects you from',
    protects: [
      {
        title: 'Vault breaches',
        body: 'There is no password file to leak, no backup to lose, no sync to intercept.',
      },
      {
        title: 'Password reuse',
        body: 'Every service gets an independent password. A breach at site A says nothing about site B.',
      },
      {
        title: 'GPU brute force',
        body: 'Argon2id demands 64 MiB per attempt, which wrecks the massive parallelism that shreds fast hashes.',
      },
      {
        title: 'Depending on us',
        body: 'The algorithm is documented and has a 200-line reference implementation. If this site disappears, your passwords remain recoverable.',
      },
    ],
    limitsTitle: 'Does not protect you from',
    limits: [
      {
        title: 'A weak master password',
        body: 'Argon2id makes each guess expensive, but it cannot save a master that already sits in a breach list. Use a long, unique phrase.',
      },
      {
        title: 'A compromised device',
        body: 'A keylogger, malware or a malicious extension reads your master as you type it. No derivation scheme fixes that.',
      },
      {
        title: 'Forgetting the parameters',
        body: 'Changed the length or the version and did not write it down? The password changes. Record the recipe or always stick to the standard format.',
      },
      {
        title: 'Forced password rotation',
        body: 'Some sites require periodic changes. Use the Version field — but remember which number is currently live.',
      },
    ],
  },

  compat: {
    title: 'In practice',
    subtitle: 'Tips for the real world, where forms do not always cooperate.',
    tips: [
      {
        title: 'The site rejected the password',
        body: 'Use the Compatible format: 12 characters, no symbols and no ambiguous characters gets past almost every stubborn validator.',
      },
      {
        title: 'You need to rotate a password',
        body: 'Bump Version from 1 to 2. The password changes completely and your master stays untouched.',
      },
      {
        title: 'Typing on a TV or console',
        body: 'The Letters only format gives 20 lowercase characters: still ~94 bits of entropy, without hunting for symbols on an on-screen keyboard.',
      },
      {
        title: 'No internet',
        body: 'After the first visit the page works offline. Install it as an app and it stays available in airplane mode.',
      },
      {
        title: 'Migrating gradually',
        body: 'Do not convert everything at once. Start with the critical accounts — email and banking — and move the rest as you use them.',
      },
      {
        title: 'Store the recipe, not the password',
        body: 'Write down service, login, version and format on paper or in a note. None of it is secret, and it is enough to regenerate everything.',
      },
    ],
  },

  faq: {
    title: 'Frequently asked questions',
    items: [
      {
        q: 'What happens if I forget my master password?',
        a: 'You lose access to every derived password, with no recovery — there is no server and no copy. That is the direct trade-off of storing nothing. Choose a memorable phrase and, if it makes sense for you, keep a physical copy somewhere safe.',
      },
      {
        q: 'Is this safer than a traditional password manager?',
        a: 'It is a different trade-off, not strictly better. You swap the risk of a vault breach for having to remember the parameters and for not being able to rotate a single password without the Version field. Traditional managers also store useful metadata — security questions, recovery codes — that this method does not cover.',
      },
      {
        q: 'Is anything sent to a server?',
        a: 'No. The page is a static file served by GitHub Pages and makes no requests after loading — no analytics, no CDN, no external fonts. You can verify this in the browser Network tab, or simply turn off your internet and use it anyway.',
      },
      {
        q: 'Why does the button take a second or two?',
        a: 'The slowness is the security feature. Argon2id spends 64 MiB of memory and three passes on every derivation. For you that happens once; for someone guessing your master, it is that cost multiplied by billions of attempts.',
      },
      {
        q: 'Can I use it on several devices?',
        a: 'Yes, and that is the whole point: the same algorithm with the same inputs produces the same password on your phone, your laptop and someone else’s browser. There is nothing to sync.',
      },
      {
        q: 'Why does the password change when I adjust the length?',
        a: 'The format is part of the salt on purpose. That makes variants of the same account mathematically independent: whoever obtains the 12-character version learns nothing about the 32-character one.',
      },
      {
        q: 'How do I know the code does what it claims?',
        a: 'The entire cryptographic core fits in five small files, the specification is written out in plain prose, and there is a zero-dependency reference implementation in Node. The test suite compares both implementations character by character.',
      },
      {
        q: 'What is the fingerprint next to the master password?',
        a: 'Three symbols derived only from your master. Because they appear before you generate anything, they catch a typo immediately. In exchange, they expose 24 bits of a fast hash of your master — so they should not be photographed or shared, and they can be turned off.',
      },
    ],
  },

  footer: {
    spec: 'Algorithm specification',
    code: 'Source code',
    reference: 'Reference implementation',
    license: 'MIT license',
    disclaimer:
      'Free software, provided as is, without warranty. Review the code before trusting it with your accounts.',
    offline: 'Works offline',
  },
}
